import type {
	CommonProps,
	CustomProps,
	ILogForwarder,
	ILogger,
	IRemoteLogForwarder,
	LogFuncWithProps,
	LogMessage,
	LogType,
	LogValue,
	MetricType,
} from "./types";
import { LogLevel } from "./types";
import { normalizeLogValue } from "./utils/normalize";

function canFlushLogs(forwarder: ILogForwarder): forwarder is ILogForwarder & Pick<IRemoteLogForwarder, "flushLogs"> {
	return "flushLogs" in forwarder && typeof forwarder.flushLogs === "function";
}

export class LogRouter implements ILogger, IRemoteLogForwarder {
	private _criticalLogger: ILogForwarder;
	private _trivialLogger: ILogForwarder;
	private _trace!: LogFuncWithProps;
	private _debug!: LogFuncWithProps;
	private _info!: LogFuncWithProps;
	private _warning!: LogFuncWithProps;
	private _error!: LogFuncWithProps;
	private _fatal!: LogFuncWithProps;

	private _injectedProps: CommonProps;
	private _logLevel!: LogLevel;
	/**
	 * 2-way logging (Axiom / Console). @param LogLevel - logs below (more severe) route to
	 * criticalLogger, the rest to trivialLogger. @param criticalLogger @param trivialLogger
	 * @param injectedProps - included in every log.
	 */
	constructor(
		logLevel: LogLevel,
		criticalLogger: ILogForwarder,
		trivialLogger: ILogForwarder,
		injectedProps: CommonProps = {},
	) {
		this._criticalLogger = criticalLogger;
		this._trivialLogger = trivialLogger;
		this.changeLogLevel(logLevel);
		this._injectedProps = injectedProps;
	}
	/**
	 * Flush any buffered logs in the underlying forwarders. Fire-and-forget:
	 * always resolves, never rejects, and bounds its own latency via `timeoutMs`.
	 */
	public async flushLogs(timeoutMs: number = 5000): Promise<void> {
		const safeFlush = (forwarder: ILogForwarder): Promise<void> => {
			if (!canFlushLogs(forwarder)) return Promise.resolve();

			return new Promise<void>((resolve) => {
				const timer = setTimeout(resolve, timeoutMs);
				Promise.resolve()
					.then(() => forwarder.flushLogs(timeoutMs))
					.catch((): undefined => undefined)
					.finally(() => {
						clearTimeout(timer);
						resolve();
					});
			});
		};

		await Promise.all([safeFlush(this._criticalLogger), safeFlush(this._trivialLogger)]);
	}
	public changeLogLevel(logLevel: LogLevel) {
		this._logLevel = logLevel;
		this._info = this.getLoggerForLevel(LogLevel.info);
		this._debug = this.getLoggerForLevel(LogLevel.debug);
		this._warning = this.getLoggerForLevel(LogLevel.warn);
		this._trace = this.getLoggerForLevel(LogLevel.trace);
		this._error = this.getLoggerForLevel(LogLevel.error);
		this._fatal = this.getLoggerForLevel(LogLevel.fatal);
	}
	public injectCommonProps(injectedProps: CommonProps): LogRouter {
		this._injectedProps = { ...this._injectedProps, ...injectedProps };
		return this;
	}
	public injectCommonProp(propName: keyof CommonProps, value: string | MetricType[]): LogRouter {
		this._injectedProps[propName] = value as LogValue;
		return this;
	}

	public injectCustomProps(injectedProps: CustomProps): LogRouter {
		this._injectedProps = { ...this._injectedProps, ...injectedProps };
		return this;
	}
	public injectCustomProp(propName: string, value: LogValue): LogRouter {
		this._injectedProps[propName] = value;
		return this;
	}
	/**
	 * Reads an already-injected property.
	 * @param propName - The name of the injected prop to read
	 * @returns The injected value, or undefined when it was never injected
	 */
	public getInjectedProp(propName: keyof CommonProps): LogValue | undefined {
		return this._injectedProps[propName];
	}
	public clearInjectedProps() {
		this._injectedProps = {};
	}

	public cloneTransientInstance(): LogRouter {
		return new LogRouter(
			this._logLevel,
			this._criticalLogger,
			this._trivialLogger,
			structuredClone(this._injectedProps),
		);
	}

	private getLoggerForLevel(level: LogLevel): LogFuncWithProps {
		return level < this._logLevel
			? this._criticalLogger.log.bind(this._criticalLogger)
			: this._trivialLogger.log.bind(this._trivialLogger);
	}

	private static normalizeMessage(message: unknown): LogMessage {
		if (typeof message === "string") return message;
		if (message == null) return "";
		if (Array.isArray(message) || typeof message !== "object") {
			return LogRouter.stringifyForMessage(message);
		}
		// Object messages carry the same secret-leak risk as optionalParams
		// (`logger.error("x", { apiToken: "..." })` puts the token on the MESSAGE,
		// not a trailing param) — route it through the same normalizeLogValue
		// redaction/circular/depth handling before it ever reaches a forwarder.
		const normalized = normalizeLogValue(message);
		if (
			normalized === null ||
			typeof normalized !== "object" ||
			Array.isArray(normalized) ||
			normalized instanceof Error
		) {
			// Only a non-plain-record message (e.g. a class instance normalizeLogValue
			// tags as `[ClassName]`, or the rare `Error` passed directly as the
			// message rather than as metadata) takes this path — LogMessage's object
			// variant is always `Record<string, LogValue>` in practice.
			return LogRouter.stringifyForMessage(message);
		}
		const objectMessage = normalized;
		if (typeof objectMessage.message === "string" && objectMessage.message.length > 0) {
			return objectMessage;
		}

		return {
			...objectMessage,
			message: LogRouter.deriveMessageString(objectMessage),
		};
	}

	private static deriveMessageString(message: Record<string, LogValue>): string {
		// The observability seams pass { event: "domain.outcome", ...fields } —
		// the message renders as a HUMANIZED sentence while the exact slug ships
		// verbatim in the top-level `event` field (the machine key for queries
		// and soaks). Falling through to JSON made every seam log render as a
		// 200-char blob; rendering the raw slug read as machine noise.
		if (typeof message.event === "string" && message.event.length > 0) {
			return LogRouter.humanizeEventSlug(message.event);
		}
		if (typeof message.action === "string" && message.action.length > 0) return message.action;
		if (typeof message.name === "string" && message.name.length > 0) return message.name;

		const metrics = message.metrics;
		if (metrics && typeof metrics === "object" && !Array.isArray(metrics)) {
			const metricName = (metrics as Record<string, LogValue>).name;
			if (typeof metricName === "string" && metricName.length > 0) return metricName;
		}

		return LogRouter.stringifyForMessage(message);
	}

	/**
	 * `domain.outcome_slug` → `"Domain: outcome slug"`. Deterministic — dots
	 * split domain from outcome, underscores/hyphens become spaces, first letter
	 * capitalized — so there is no per-event copy to write or drift. The slug
	 * itself is never lost: it ships as the `event` field on the same entry.
	 */
	private static humanizeEventSlug(slug: string): string {
		const words = (segment: string) => segment.replace(/[_-]+/g, " ").trim();
		const [domain, ...outcome] = slug.split(".");
		const head = words(domain ?? "");
		const capitalized = head.length > 0 ? head[0]?.toUpperCase() + head.slice(1) : head;
		if (outcome.length === 0) return capitalized;
		return `${capitalized}: ${outcome.map(words).join(" ")}`;
	}

	private static stringifyForMessage(value: unknown): string {
		let str: string;
		if (typeof value !== "object" || value === null) {
			str = String(value);
		} else {
			try {
				const json = JSON.stringify(value);
				str = typeof json === "string" ? json : String(value);
			} catch {
				return "[unserializable]";
			}
		}

		return str.length > 200 ? `${str.slice(0, 197)}...` : str;
	}

	log(level: LogLevel, type: LogType, message: LogMessage, ...optionalParams: unknown[]) {
		switch (level) {
			case LogLevel.trace:
				this.trace(type, message, ...optionalParams);
				break;
			case LogLevel.debug:
				this.debug(type, message, ...optionalParams);
				break;
			case LogLevel.info:
				this.info(type, message, ...optionalParams);
				break;
			case LogLevel.warn:
				this.warning(type, message, ...optionalParams);
				break;
			case LogLevel.error:
				this.error(type, message, ...optionalParams);
				break;
			case LogLevel.fatal:
				this.fatal(type, message, ...optionalParams);
				break;
		}
	}
	info = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => {
		this._info(
			type,
			LogLevel.info,
			this._injectedProps,
			LogRouter.normalizeMessage(message),
			...optionalParams.map((param) => normalizeLogValue(param)),
		);
	};
	debug = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => {
		this._debug(
			type,
			LogLevel.debug,
			this._injectedProps,
			LogRouter.normalizeMessage(message),
			...optionalParams.map((param) => normalizeLogValue(param)),
		);
	};

	error = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => {
		this._error(
			type,
			LogLevel.error,
			this._injectedProps,
			LogRouter.normalizeMessage(message),
			...optionalParams.map((param) => normalizeLogValue(param)),
		);
	};

	warning = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => {
		this._warning(
			type,
			LogLevel.warn,
			this._injectedProps,
			LogRouter.normalizeMessage(message),
			...optionalParams.map((param) => normalizeLogValue(param)),
		);
	};

	trace = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => {
		this._trace(
			type,
			LogLevel.trace,
			this._injectedProps,
			LogRouter.normalizeMessage(message),
			...optionalParams.map((param) => normalizeLogValue(param)),
		);
	};

	fatal = (type: LogType, message: LogMessage, ...optionalParams: unknown[]) => {
		this._fatal(
			type,
			LogLevel.fatal,
			this._injectedProps,
			LogRouter.normalizeMessage(message),
			...optionalParams.map((param) => normalizeLogValue(param)),
		);
	};

	metric = (level: LogLevel, message: LogMessage, metrics: MetricType) => {
		this.log(level, "METRIC", { message, metrics } as Record<string, LogValue>);
	};
}
